// The PF window (Architecture D-75; API.md §3.3; Frontend §6.6), computed from the **draft's** locations with the same
// rules as the backend (`modules/clinic-settings/pf-window.ts`, T-U-SLOT-29): over the locations that are shown, not
// removed in PF and have PF hours, a weekday is PF-open if one of them is open, from the earliest open to the latest
// close. No such location → no window (no limit). Recomputed on every draft change.
import { TIME_OPTIONS, toMinutes } from './hours'
import type { Location, Settings } from './types'

/** Minutes of the clinic-local day. */
export interface PfDayWindow {
  open: number
  close: number
}

/** Index = weekday (0 = Sunday); `null` = not PF-open that day. */
export type PfWindow = readonly (PfDayWindow | null)[]

export function pfWindow(locations: readonly Pick<Location, 'visible' | 'removedInPf' | 'pfHours'>[]): PfWindow | null {
  const counted = locations.filter((l) => l.visible && !l.removedInPf && l.pfHours !== null)
  if (counted.length === 0) return null
  return [0, 1, 2, 3, 4, 5, 6].map((weekday) => {
    let window: PfDayWindow | null = null
    for (const l of counted) {
      const day = l.pfHours?.find((h) => h.weekday === weekday)
      if (!day?.open) continue
      const open = toMinutes(day.start)
      const close = toMinutes(day.end)
      // Overnight or empty PF hours aren't expected; they count as closed (task 06a assumption).
      if (!(close > open)) continue
      window = window ? { open: Math.min(window.open, open), close: Math.max(window.close, close) } : { open, close }
    }
    return window
  })
}

/**
 * The window C-10 and the C-04 problems apply (D-75): the draft's shown locations. None (no limit) while a super admin
 * has the clinic's testing switch on (`testingIgnorePfHours`, read-only here).
 */
export const draftPfWindow = (s: Settings): PfWindow | null => (s.testingIgnorePfHours ? null : pfWindow(s.locations))

/**
 * C-10 picker options for one day: the 30-minute times (5:00 AM – 11:00 PM) inside the day's window, plus `current`
 * when it falls outside (shown with the inline error). `undefined` window = no limit; `null` = not PF-open.
 */
export function timeOptionsIn(window: PfDayWindow | null | undefined, current?: string) {
  if (window === undefined) return TIME_OPTIONS
  const inside = TIME_OPTIONS.filter(
    (o) => window !== null && toMinutes(o.value) >= window.open && toMinutes(o.value) <= window.close,
  )
  const extra = current !== undefined ? TIME_OPTIONS.find((o) => o.value === current) : undefined
  if (!extra || inside.some((o) => o.value === current)) return inside
  return [...inside, extra].sort((a, b) => toMinutes(a.value) - toMinutes(b.value))
}

/**
 * Whether a closed day can be opened on C-10: PF-open with at least two picker times inside its window (a window too
 * short for an opening and a closing time counts as closed; task 06a assumption). No window → always.
 */
export function canOpenDay(window: PfWindow | null, weekday: number): boolean {
  if (window === null) return true
  return timeOptionsIn(window[weekday] ?? null).length >= 2
}
